import {
	Button,
	FormControl,
	FormErrorMessage,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	VStack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import AppInput from "../Core/AppInput/AppInput";
import { useSetConsultantPassword } from "../../networking/consultant.hooks";
import { useSetCustomerUserPassword } from "../../networking/customer.hooks";
import { PASSWORD_REGEX } from "../../utils/ValidationRules";
import { toast } from "../../utils/toast";

interface Props {
	isOpen: boolean;
	onClose: () => void;
	userId: string;
	/** Which kind of account the password is set for. Defaults to consultant. */
	userType?: "consultant" | "customerUser";
}

type Inputs = {
	newPassword: string;
	confirmPassword: string;
};

const validationSchema = yup.object().shape({
	newPassword: yup.string().required("PASSWORD_REQUIRED").matches(PASSWORD_REGEX, {
		message: "PASSWORD_VALIDATION_MESSAGE",
		excludeEmptyString: true,
	}),
	confirmPassword: yup
		.string()
		.required("PASSWORD_REQUIRED")
		.oneOf([yup.ref("newPassword")], "PASSWORD_MISMATCH"),
});

const SetPasswordModal: React.FC<Props> = ({
	isOpen,
	onClose,
	userId,
	userType = "consultant",
}) => {
	const { t } = useTranslation();
	const consultantMutation = useSetConsultantPassword();
	const customerUserMutation = useSetCustomerUserPassword();
	const { mutate: setPassword, isLoading } =
		userType === "customerUser" ? customerUserMutation : consultantMutation;

	const { register, handleSubmit, formState, reset } = useForm<Inputs>({
		mode: "onBlur",
		resolver: yupResolver(validationSchema),
	});

	const onSubmit = handleSubmit((data) => {
		setPassword(
			{ userId, newPassword: data.newPassword },
			{
				onSuccess: () => {
					toast.success(t("SET_PASSWORD_SUCCESS"));
					reset();
					onClose();
				},
			}
		);
	});

	const handleClose = () => {
		reset();
		onClose();
	};

	return (
		<Modal closeOnOverlayClick={false} onClose={handleClose} size="md" isOpen={isOpen}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{t("SET_PASSWORD")}</ModalHeader>
				<ModalCloseButton />
				<form onSubmit={onSubmit}>
					<ModalBody>
						<VStack gap={4} alignItems="flex-start">
							<FormControl isInvalid={!!formState.errors.newPassword}>
								<AppInput
									{...register("newPassword")}
									placeholder="PASSWORD"
									icon="password"
									type="password"
									errors={
										formState.errors.newPassword?.message
											? t(formState.errors.newPassword.message)
											: undefined
									}
								/>
								{formState.errors.newPassword && (
									<FormErrorMessage>
										{t(formState.errors.newPassword.message as string)}
									</FormErrorMessage>
								)}
							</FormControl>
							<FormControl isInvalid={!!formState.errors.confirmPassword}>
								<AppInput
									{...register("confirmPassword")}
									placeholder="pwReset.confirmPassword"
									icon="password"
									type="password"
									errors={
										formState.errors.confirmPassword?.message
											? t(formState.errors.confirmPassword.message)
											: undefined
									}
								/>
								{formState.errors.confirmPassword && (
									<FormErrorMessage>
										{t(formState.errors.confirmPassword.message as string)}
									</FormErrorMessage>
								)}
							</FormControl>
						</VStack>
					</ModalBody>
					<ModalFooter gap={3}>
						<Button colorScheme="green" type="submit" isLoading={isLoading}>
							{t("SEND")}
						</Button>
						<Button onClick={handleClose}>{t("CANCEL")}</Button>
					</ModalFooter>
				</form>
			</ModalContent>
		</Modal>
	);
};

export default SetPasswordModal;
